import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { folderTypes } from "@redotech/redo-model/views/folders";
import { WorkOrderView } from "@redotech/redo-model/views/views";
import {
  ReactNode,
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { CreateOmsViewFolderModal } from "../common/create-oms-view-folder-modal";
import { EditOmsViewFolderModal } from "../common/edit-oms-view-folder-modal";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";

export interface WorkOrderViewsData {
  openCreateFolderModal: () => void;
  openEditFolderModal: (folderId: string) => void;
  handleDeleteFolder: (folderId: string) => void;
  updateOrdering: (
    updatedViews: WorkOrderView[],
    updatedFolders: folderTypes["work-orders"][],
  ) => void;
  hasInitallyLoaded: boolean;
  views: WorkOrderView[];
  folders: folderTypes["work-orders"][];
}

export const WorkOrderViewsContext = createContext<
  WorkOrderViewsData | undefined
>(undefined);

export type ViewCounts = Record<string, number>;
export type FolderCounts = Record<string, number>;

export const WorkOrderViewCountsContext = createContext<
  { viewCounts: ViewCounts; folderCounts: FolderCounts } | undefined
>(undefined);

export const ReloadWorkOrderViewsContext = createContext<
  (() => Promise<void>) | undefined
>(undefined);

export const WorkOrderViewsProvider = memo(function WorkOrderViewsProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const client = useContext(RedoOutboundLabelsRpcClientContext);

  const [views, setViews] = useState<WorkOrderView[]>([]);
  const [folders, setFolders] = useState<folderTypes["work-orders"][]>([]);
  const [viewCounts, setViewCounts] = useState<Record<string, number>>({});
  const [folderCounts, setFolderCounts] = useState<Record<string, number>>({});
  const [hasInitallyLoaded, setHasInitallyLoaded] = useState(false);

  const [_, loadViews] = useTriggerLoad(async (signal) => {
    if (!client) {
      return;
    }

    const { views, folders } = await client.getWorkOrderViewsAndFolders(null, {
      signal,
    });
    setHasInitallyLoaded(true);
    setViews(views);
    setFolders(folders);
  });

  const reloadViews = useHandler(async () => {
    loadViews();
  });

  useEffect(() => {
    void reloadViews();
  }, [client, reloadViews]);

  useLoad(
    async (signal) => {
      if (!client || !hasInitallyLoaded) {
        return;
      }
      const result = await client.getWorkOrderViewCounts(null, { signal });
      setViewCounts(result.viewCounts);
      setFolderCounts(result.folderCounts);
    },
    [client, hasInitallyLoaded, views],
  );

  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showEditFolderModal, setShowEditFolderModal] = useState(false);

  const [selectedFolder, setSelectedFolder] = useState<
    folderTypes["work-orders"] | null
  >(null);

  const handleCreateFolder = async (name: string) => {
    if (!client) {
      return;
    }
    await client.createWorkOrderViewFolder({ name });
    void reloadViews();
  };

  const handleEditFolder = async (folderId: string, folderName: string) => {
    if (!client) {
      return;
    }
    await client.updateWorkOrderViewFolder({ folderId, name: folderName });
    void reloadViews();
  };

  const handleDeleteFolder = async (folderId: string) => {
    if (!client) {
      return;
    }
    await client.deleteWorkOrderViewFolder({ folderId });
    void reloadViews();
  };

  const updateOrdering = async (
    updatedViews: WorkOrderView[],
    updatedFolders: folderTypes["work-orders"][],
  ) => {
    if (!client) {
      return;
    }
    setViews(updatedViews);
    setFolders(updatedFolders);

    try {
      await client.saveWorkOrderViewsAndFolders({
        views: updatedViews,
        folders: updatedFolders,
      });
    } catch (error) {
      console.error("Error saving work order view and folder ordering");
      if (error instanceof Error) {
        console.error(error.message);
      } else {
        console.error(error);
      }
    }
  };

  const openCreateFolderModal = useHandler(() => {
    setShowCreateFolderModal(true);
  });

  const openEditFolderModal = useHandler((folderId: string) => {
    const folder = folders?.find((folder) => folder._id === folderId);
    if (folder) {
      setShowEditFolderModal(true);
      setSelectedFolder(folder);
    }
  });

  const reservedFolderNames = useMemo(() => {
    return new Set(folders.map((folder) => folder.name));
  }, [folders]);

  return (
    <WorkOrderViewsContext.Provider
      value={{
        openCreateFolderModal,
        openEditFolderModal,
        handleDeleteFolder,
        updateOrdering,
        views,
        folders,
        hasInitallyLoaded,
      }}
    >
      <ReloadWorkOrderViewsContext.Provider value={reloadViews}>
        <WorkOrderViewCountsContext.Provider
          value={{ viewCounts, folderCounts }}
        >
          {children}
        </WorkOrderViewCountsContext.Provider>
        <CreateOmsViewFolderModal
          isOpen={showCreateFolderModal}
          onClose={() => setShowCreateFolderModal(false)}
          onSubmit={handleCreateFolder}
          reservedFolderNames={reservedFolderNames}
        />
        {selectedFolder && (
          <EditOmsViewFolderModal
            deleteFolder={handleDeleteFolder}
            isOpen={showEditFolderModal}
            onClose={() => setShowEditFolderModal(false)}
            onSubmit={handleEditFolder}
            reservedFolderNames={reservedFolderNames}
            selectedFolder={selectedFolder}
          />
        )}
      </ReloadWorkOrderViewsContext.Provider>
    </WorkOrderViewsContext.Provider>
  );
});
